/**
 * Pure token-resolution logic for the playground Firebase client, extracted from
 * the `getFirebaseClient` closure so it can be unit-tested without spinning up
 * Firebase init or a live backend. The closure supplies the injected deps.
 */

type User = import('firebase/auth').User

/** The subset of the Firebase `User` this logic depends on. */
export type TokenRefreshUser = Pick<User, 'getIdToken' | 'email'>

export interface ResolveIdTokenDeps {
  /** The current Firebase user, or null when signed out. */
  currentUser: TokenRefreshUser | null
  /** True for the local Auth-emulator target. */
  isLocal: boolean
  /**
   * Local-emulator recovery: re-authenticate the given email and return a fresh
   * ID token, or null if no user results. Preserves the closure's existing
   * forced-refresh-on-fresh-user behavior; only invoked on the local branch.
   */
  reAuthLocal: (email: string) => Promise<string | null>
  /** Sign out the active auth (last-resort local recovery). */
  signOutLocal: () => Promise<void>
  /** Log the underlying refresh error before mapping (defaults to console.warn in the closure). */
  warn: (message: string, err: unknown) => void
}

const SESSION_DEATH_CODES = new Set<string>([
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-user-token',
])

const TRANSIENT_CODES = new Set<string>([
  'auth/quota-exceeded',
  'auth/too-many-requests',
  'auth/network-request-failed',
  'auth/timeout',
])

export function extractAuthErrorCode(err: unknown): string | undefined {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code?: unknown }).code
    if (typeof code === 'string') {
      return code
    }
  }
  return undefined
}

/**
 * Map a non-local refresh failure to a thrown Error. Session death keeps the
 * historical wording; transient/network failures get a distinct, retry-worthy
 * message; unknown codes are surfaced verbatim. Downstream classifiers key off
 * the message text, so keep these strings stable and distinct.
 */
export function buildNonLocalRefreshError(refreshError: unknown): Error {
  const code = extractAuthErrorCode(refreshError)
  if (code && SESSION_DEATH_CODES.has(code)) {
    return new Error('[RUN] Session expired. Sign in again via the toolbar.')
  }
  if (code && TRANSIENT_CODES.has(code)) {
    return new Error(
      `[RUN] Auth token refresh failed transiently (${code}). This is a rate-limit or network error — retry the request.`,
    )
  }
  return new Error(
    `[RUN] Auth token refresh failed (${code ?? 'unknown'}). See console for the underlying error.`,
  )
}

export async function resolveIdTokenOrThrow(deps: ResolveIdTokenDeps): Promise<string> {
  const { currentUser, isLocal } = deps

  if (!currentUser) {
    const hint = isLocal
      ? 'Click the RUN.world Playground toolbar to sign in.'
      : 'Sign in via the toolbar.'
    throw new Error(`[RUN] Not signed in. ${hint}`)
  }

  try {
    return await currentUser.getIdToken()
  } catch (refreshError) {
    deps.warn('[RUN] Token refresh failed', refreshError)

    if (isLocal) {
      // NEVER signOut() first in the emulator — it revokes ALL outstanding tokens
      // and cascades auth/id-token-revoked. Re-authenticate to mint a fresh session.
      const email = currentUser.email
      if (email) {
        try {
          const freshToken = await deps.reAuthLocal(email)
          if (freshToken) {
            return freshToken
          }
        } catch (reAuthError) {
          deps.warn('[RUN] Local re-auth failed', reAuthError)
        }
      }
      await deps.signOutLocal()
      throw new Error('[RUN] Session expired. Please sign in again via the RUN.world toolbar.')
    }

    throw buildNonLocalRefreshError(refreshError)
  }
}
